import { MemberAlias, MemberMap } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useMembersWithAlias } from 'chat/members/queries/list'
import { useSearchRecipients } from 'chat/members/queries/recipients'
import { useUpdateMembers } from 'chat/members/queries/update'
import { ProcessingMemberListView } from 'chat/members/views/processing_member_list'
import { SelectMembersView } from 'chat/members/views/select'
import { AddMembersScreenProps } from 'home/stack/types'
import { isEqual } from 'lodash'
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'

export function AddMembersScreen({ navigation, route }: AddMembersScreenProps) {
  const { conversationId, companyId } = route.params
  const [searchTerm, setSearchTerm] = useState<string>('')
  const searchRecipientsQuery = useSearchRecipients(searchTerm, companyId)
  const membersQuery = useMembersWithAlias(conversationId)
  const updateMembersMutation = useUpdateMembers()
  const disableAliasSelection = searchTerm.length > 0

  const nonAliasMembers = useMemo(() => {
    return membersQuery.data?.filter(m => !m.isAlias)
  }, [membersQuery.data])

  const memberAliases: MemberAlias[] | undefined = useMemo(() => {
    return membersQuery.data
      ?.filter(m => m.isAlias)
      .map(m => m.firstName as MemberAlias)
  }, [membersQuery.data])

  const currentMemberMap = useMemo(
    () =>
      nonAliasMembers?.reduce<MemberMap>(
        (map, member) => ({ ...map, [member.id]: member }),
        {}
      ),
    [nonAliasMembers]
  )

  const currentMemberIds = useMemo(
    () => currentMemberMap && Object.keys(currentMemberMap),
    [currentMemberMap]
  )

  const [selectedMembers, setSelectedMembers] = useState<MemberMap>()
  const [selectedMemberAliases, setSelectedMemberAliases] = useState<
    MemberAlias[] | undefined
  >()

  useLayoutEffect(() => {
    if (currentMemberMap && !selectedMembers) {
      setSelectedMembers(currentMemberMap)
    }
  }, [currentMemberMap, selectedMembers])

  useLayoutEffect(() => {
    if (memberAliases && selectedMemberAliases === undefined) {
      setSelectedMemberAliases(memberAliases)
    }
  }, [memberAliases, selectedMemberAliases])

  const updateMembers = useCallback(async () => {
    await updateMembersMutation.mutateAsync({
      conversationId,
      members: Object.values(selectedMembers!),
      memberAliases: selectedMemberAliases || [],
    })
    navigation.pop()
  }, [
    conversationId,
    navigation,
    selectedMemberAliases,
    selectedMembers,
    updateMembersMutation,
  ])

  const { theme } = useTheme()

  const submitDisabled = useMemo(() => {
    const membersUnchanged = selectedMembers
      ? isEqual(selectedMembers, currentMemberMap)
      : true

    const aliasesUnchanged = selectedMemberAliases
      ? isEqual(selectedMemberAliases, memberAliases ?? [])
      : true

    return membersUnchanged && aliasesUnchanged
  }, [selectedMembers, currentMemberMap, selectedMemberAliases, memberAliases])

  useLayoutEffect(() => {
    showHeaderButton({
      navigation,
      theme,
      title: t('add'),
      onPress: updateMembers,
      disabled: submitDisabled,
      position: 'right',
    })
  }, [navigation, theme, updateMembers, submitDisabled])

  // FIXME: since members mutation optimistically updates, don't show loading indicator, but still catch error

  if (searchRecipientsQuery.isLoading) {
    return <ProcessingMemberListView />
  }

  return (
    <StatusBoundary
      loadingMessage={
        searchRecipientsQuery.isLoading
          ? t('loadingRecipientsText')
          : updateMembersMutation.isPending
          ? t('saving')
          : undefined
      }
      isLoading={
        searchRecipientsQuery.isLoading ||
        updateMembersMutation.isPending ||
        membersQuery.isLoading
      }
      error={
        searchRecipientsQuery.error ??
        membersQuery.error ??
        updateMembersMutation.error
      }
    >
      <SelectMembersView
        recipients={searchRecipientsQuery.data}
        selectedMembers={selectedMembers ?? {}}
        disabledMemberIds={currentMemberIds}
        onMembersSelected={setSelectedMembers}
        onMemberAliasesSelected={setSelectedMemberAliases}
        memberAliases={selectedMemberAliases}
        searchText={searchTerm}
        setSearchText={setSearchTerm}
        disableAliasSelection={disableAliasSelection}
      />
    </StatusBoundary>
  )
}
